import React, { useState, useRef } from "react";
import { Chip, Grid, Typography, Box, InputBase } from "@mui/material";
import { colorSwapIconHoverSx } from "@/styles/iconHover";

interface MultiChipTextFieldProps {
    values: string[];
    onChange: (values: string[]) => void;
    label: string;
    size?: number;
    placeholder?: string;
}

export const MultiChipTextField: React.FC<MultiChipTextFieldProps> = ({
                                                                          label,
                                                                          onChange,
                                                                          values = [],
                                                                          size = 12,
                                                                            placeholder = ''
                                                                      }) => {
    const [editMode, setEditMode] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string>("");
    const inputRef = useRef<HTMLInputElement>(null);

    const handleAddChip = () => {
        const trimmedValue = inputValue.trim();
        if (trimmedValue && !values.includes(trimmedValue)) {
            onChange([...values, trimmedValue]);
        }
        setInputValue("");
    };

    const handleDeleteChip = (chipToDelete: string) => {
        onChange(values.filter((v) => v !== chipToDelete));
    };

    return (
        <Grid container size={{ xs: size }} rowGap={0.6}>
            <Grid size={{ xs: 12 }}>
                <Typography
                    variant="body1"
                    sx={{
                        alignContent: "center",
                        height: "100%",
                        color: "#fff",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {label}
                </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
                <Box
                    onClick={() => {
                        if (!editMode) setEditMode(true);
                        // Focus immediato sull'input appena si clicca l'area
                        setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                    sx={{
                        display: "flex",
                        flexWrap: "wrap", // Se i chip sono troppi, vanno dolcemente a capo
                        gap: 1, // Spazio orizzontale e verticale tra chip e testo
                        alignItems: "center",
                        width: "100%",
                        minHeight: 28,
                        cursor: "text",
                        // Linea inferiore in stile TextField variant="standard"
                        borderBottom: editMode ? "2px solid #90caf9" : "1px solid rgba(255,255,255,0.5)",
                        pb: 0.5,
                        transition: "border-color 0.2s ease",
                        "&:hover": {
                            borderBottom: editMode ? "2px solid #90caf9" : "1px solid #fff",
                        }
                    }}
                >
                    {/* 1. I chip renderizzati in linea */}
                    {values.map((val, idx) => (
                        <Chip
                            key={idx}
                            label={val}
                            size="small"
                            onDelete={() => handleDeleteChip(val)}
                            // IL TRUCCO: previene la perdita di focus dall'input quando si clicca la X
                            onMouseDown={(e) => e.preventDefault()}
                            sx={{
                                height: 22,
                                color: "#fff",
                                backgroundColor: "transparent",
                                border: "1px solid rgba(255,255,255,0.3)",
                                "& .MuiChip-deleteIcon": colorSwapIconHoverSx({ color: "rgba(255,255,255,0.5)", hoverColor: "#fff" })
                            }}
                        />
                    ))}

                    {/* 2. L'input per scrivere, messo subito dopo l'ultimo chip */}
                    {editMode && (
                        <InputBase
                            inputRef={inputRef}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onBlur={() => {
                                handleAddChip();
                                setEditMode(false);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleAddChip();
                                } else if (e.key === "Backspace" && inputValue === "") {
                                    if (values.length > 0) {
                                        handleDeleteChip(values[values.length - 1]);
                                    }
                                }
                            }}
                            sx={{
                                color: "#fff",
                                flexGrow: 1, // Prende lo spazio restante sulla riga
                                minWidth: 60, // Non si restringe troppo prima di andare a capo
                                height: 22,
                                "& input": { padding: 0 } // Allineamento perfetto coi chip
                            }}
                        />
                    )}

                    {/* Placeholder se è vuoto e non stiamo scrivendo */}
                    {!editMode && values.length === 0 && (
                        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.4)" }}>
                            {placeholder}
                        </Typography>
                    )}
                </Box>
            </Grid>
        </Grid>
    );
};